/**
 * COC daily report view-model (COC-EOD-REPORT-V1, activity 23). PURE: no
 * fetch, no React.
 *
 * DATE NAVIGATION (#632 evidence-harness + shared-component corrective):
 * this module owns NO date-nav arithmetic. The day axis is the ONE
 * registered day-scope primitive (frontend/src/dashboard/ui/day-scope.ts +
 * DateCommandBar.tsx + useDayScope) -- the SAME primitive the Appointments
 * planning calendar and the Doctor's Day cockpit already consume. A prior
 * revision of this module duplicated isValidBusinessDate / addBusinessDays /
 * todayIstBusinessDate / dayLabel (mirroring the sibling call-centre
 * report's OWN un-registered copy) instead of using the registered
 * primitive; that private copy is retired here, not extended.
 *
 * REUSE, never re-derive:
 *   - The day-flow + money tiles are NOT recomputed here at all -- the page
 *     imports deriveDayCommandTiles / sumDayCommandTiles /
 *     CancelReasonBreakdownRow / DayCommandTiles / inrLabel directly from
 *     manager-tracking/manager-cockpit-view.ts (the SAME pure derivation
 *     ManagerOpsCockpit already renders), verbatim.
 *   - SLA breach classes: classMeta / classShortLabel / formatLeakageAge /
 *     SLA_CLASS_ORDER come from the ONE leakage view vocabulary
 *     (sla-leakage-view.ts) -- the SAME labels COC's own station
 *     (CocStationSection.tsx) already renders today.
 *
 * GENUINELY ADDED: slaBreakdownRows below. COC's existing frontend tally
 * (coc/station-view.ts stationSlaBreachCounts) already aggregates
 * CocStationRowView.slaChips into per-class COUNTS for the quiet inline
 * strip (SlaBreachStrip) -- but that aggregate discards severity, which a
 * REPORT's breakdown table needs (to show which classes are 'soon' vs
 * 'breached', matching the sibling call-centre report's own breakdown
 * shape). slaBreakdownRows performs the SAME arithmetic over the SAME
 * classifier-verbatim slaChips, with the one additional field (worst
 * severity per class) that aggregate does not carry -- never a second
 * classification, and station-view.ts itself is untouched.
 *
 * PAST-DAY HONESTY (inherited from PR #630, docs/AGENT-RUNS/
 * CALLCENTRE-EOD-V1.md): COC's SLA-breach scope is a LIVE, current-instant
 * classification (classifyLeakage runs against nowMs on every call,
 * regardless of which day is being viewed) -- it is fetched and shown ONLY
 * for today; any OTHER day (past or future -- the registered day-scope
 * primitive permits navigating either direction) renders the honest
 * "live-only" note instead. The day-flow/money tiles are DIFFERENT: they
 * read each appointment's STORED status for the requested calendar day (no
 * age-relative math, no nowMs anywhere in deriveDayCommandTiles) -- a
 * genuinely historical-or-scheduled, any-day read, so they render for any
 * date.
 */

import type { SlaSeverity } from '@/dashboard/sla-leakage/sla-leakage-view';
import {
  classMeta,
  classShortLabel,
  formatLeakageAge,
  SLA_CLASS_ORDER,
  type SlaBreachClass,
} from '@/dashboard/sla-leakage/sla-leakage-view';
import { DOCTOR_UNASSIGNED_LABEL } from '@/dashboard/appointments/ClinicDayBoard';

export type { SlaBreachClass, SlaSeverity };
export { classMeta, classShortLabel, formatLeakageAge, SLA_CLASS_ORDER };

/** One-line proportion caption, e.g. "3 of 5 clinics". Denominator 0 -> just
 *  the count (never a divide-by-zero percent, never a fake 0%). */
export function proportionLabel(count: number, total: number): string {
  if (total <= 0) return String(count);
  const pct = Math.round((count / total) * 100);
  return `${count} of ${total} (${pct}%)`;
}

// ---- Urgency ramp (docs/design/design-language/02-DESIGN-TOKENS.md 10b.1) --
// Reuses exactly the same two steps as the sibling call-centre report and
// for the identical reason: the underlying classifier (classifyLeakage,
// shared verbatim by COC's own station read) only ever distinguishes two
// non-neutral severities (amber / red). Populating the ratified 'now' step
// would require inventing a third threshold this classifier does not have.

export type UrgencyStep = 'none' | 'soon' | 'breached';

/** neutral -> none, amber -> soon, red -> breached. Deliberately never 'now'
 *  -- see the module header. */
export function urgencyStepForSeverity(severity: SlaSeverity): UrgencyStep {
  if (severity === 'red') return 'breached';
  if (severity === 'amber') return 'soon';
  return 'none';
}

// ---- SLA breach breakdown (TODAY only; folds the SAME classifier-verbatim
// slaChips the COC station renders, class + worst severity) ----------------

export interface StationSlaChipLike {
  readonly breachClass: string;
  readonly severity: SlaSeverity;
}

export interface StationRowSlaLike {
  readonly slaChips: readonly StationSlaChipLike[];
}

export interface SlaClassBreakdownRow {
  readonly breachClass: SlaBreachClass;
  readonly count: number;
  readonly severity: SlaSeverity;
}

const KNOWN_CLASSES: readonly string[] = SLA_CLASS_ORDER;

/**
 * Per-class breach counts + worst severity over COC's own scoped station
 * rows (any lawful row shape carrying slaChips -- CocStationRowView from the
 * wire, or a test fixture). Only non-zero classes are returned, in the
 * SAME SLA_CLASS_ORDER every other SLA surface in this product uses. An
 * unrecognised breachClass token (a future class this page has not been
 * taught) is skipped rather than guessed -- never renders as a fake class.
 */
export function slaBreakdownRows(
  rows: readonly StationRowSlaLike[],
): readonly SlaClassBreakdownRow[] {
  const counts = new Map<string, number>();
  const worst = new Map<string, SlaSeverity>();
  for (const row of rows) {
    for (const chip of row.slaChips) {
      if (!KNOWN_CLASSES.includes(chip.breachClass)) continue
      counts.set(chip.breachClass, (counts.get(chip.breachClass) ?? 0) + 1);
      const prev = worst.get(chip.breachClass);
      if (prev === undefined || (prev !== 'red' && chip.severity === 'red')) {
        worst.set(chip.breachClass, chip.severity);
      }
    }
  }
  return SLA_CLASS_ORDER.filter((cls) => (counts.get(cls) ?? 0) > 0).map((cls) => ({
    breachClass: cls,
    count: counts.get(cls) ?? 0,
    severity: worst.get(cls) ?? 'neutral',
  }));
}

// ---- Doctor selection filter (EXISTING-SCREEN-COMPLETIONS-V1) --------------
//
// Tenant tracker, COC home dashboard: "Individual or multi Doctor selection
// option". PURE helpers for the page's FilterBar 'tokens' group (the ONE
// registered multi-select filter primitive, ui/list/FilterBar.tsx): option
// building from the board wire's OWN doctor facts (the per-clinic
// clinicDoctors roster + each row's assignedDoctorId/Name -- no new read,
// no widened access; fences apply server-side before any filter, E-3), and
// the row predicate the page applies ONCE before every row-derived
// computation (tiles, reasons, money, per-clinic table -- counts == list by
// construction, filtered or not).

/** The explicit token for rows with NO assigned doctor. Selecting doctors
 *  must never silently drop the unassigned rows from reach -- the honest
 *  "Unassigned" state is itself selectable (DOCTOR_UNASSIGNED_LABEL is the
 *  ONE label owner, ClinicDayBoard.tsx). */
export const UNASSIGNED_DOCTOR_TOKEN = 'unassigned';

export interface DoctorFilterOption {
  readonly token: string;
  readonly label: string;
}

export interface DoctorFilterSourceClinic {
  readonly clinicName: string;
  /** The board wire's own roster (clinicDoctors; absent-normalized []). */
  readonly doctors: readonly { readonly id: number; readonly name: string }[];
  /** The same fetched rows every tile derives from. */
  readonly rows: readonly {
    readonly assignedDoctorId?: number | null;
    readonly assignedDoctorName?: string | null;
  }[];
}

/**
 * Build the doctor filter options across the COC's clinics: the union of
 * every clinic's served roster plus any doctor seen on a row but absent
 * from the roster (an inactive assignment stays reachable -- a row is never
 * silently unfilterable), plus the Unassigned token when any row carries no
 * doctor. Doctors are PER-CLINIC rows in this product (a doctor at two
 * branches is two ids), so duplicate display names across clinics are
 * disambiguated with the clinic name -- ids are the identity, labels stay
 * honest. Deterministic order: label A-Z, Unassigned last.
 */
export function doctorFilterOptions(
  perClinic: readonly DoctorFilterSourceClinic[],
): readonly DoctorFilterOption[] {
  const labelById = new Map<number, string>();
  const clinicById = new Map<number, string>();
  let hasUnassigned = false;
  for (const clinic of perClinic) {
    for (const d of clinic.doctors) {
      if (!labelById.has(d.id)) {
        labelById.set(d.id, d.name);
        clinicById.set(d.id, clinic.clinicName);
      }
    }
    for (const row of clinic.rows) {
      const id = row.assignedDoctorId;
      if (id === null || id === undefined) {
        hasUnassigned = true;
        continue;
      }
      if (!labelById.has(id)) {
        const name = row.assignedDoctorName?.trim();
        labelById.set(id, name !== undefined && name !== '' ? name : `Doctor ${id}`);
        clinicById.set(id, clinic.clinicName);
      }
    }
  }
  const nameCounts = new Map<string, number>();
  for (const name of labelById.values()) {
    nameCounts.set(name, (nameCounts.get(name) ?? 0) + 1);
  }
  const options: DoctorFilterOption[] = [...labelById.entries()]
    .map(([id, name]) => ({
      token: String(id),
      label:
        (nameCounts.get(name) ?? 0) > 1
          ? `${name} - ${clinicById.get(id) ?? ''}`.trim()
          : name,
    }))
    .sort((a, b) => (a.label < b.label ? -1 : a.label > b.label ? 1 : a.token < b.token ? -1 : 1));
  if (hasUnassigned) {
    options.push({ token: UNASSIGNED_DOCTOR_TOKEN, label: DOCTOR_UNASSIGNED_LABEL });
  }
  return options;
}

/**
 * The ONE row predicate for the doctor filter. An empty selection means NO
 * filter (every row passes -- the page's default state stays byte-identical
 * to the pre-filter page). With a selection, a row passes when its assigned
 * doctor's id is selected, or it has no assigned doctor and the Unassigned
 * token is selected.
 */
export function matchesDoctorFilter(
  row: { readonly assignedDoctorId?: number | null },
  selected: readonly string[],
): boolean {
  if (selected.length === 0) return true;
  const id = row.assignedDoctorId;
  if (id === null || id === undefined) {
    return selected.includes(UNASSIGNED_DOCTOR_TOKEN);
  }
  return selected.includes(String(id));
}
